/**
 * User Roles
 *
 * @description To quickly iterate over user roles, use `Object.values(Roles)`
 * or use `ROLES` exported from the same file.
 *
 * @example
 * Roles.ADMIN
 * Roles.REQUESTOR
 * Roles.TASK_OWNER
 */
export enum Roles {
    ADMIN = 'Admin',
    TASK_OWNER = 'Task Owner',
    REQUESTOR = 'Requestor',
    KEY_COUNTRY_USER = 'Key Country User',
    ZONE_E_COMMERCE_STEWARD = 'Zone E-Commerce Steward'
}

/**
 * Array of all `Roles` - Can be used to verify that a user-provided value is a
 * valid Roles enum value.
 *
 * @example
 *
 * ROLES.includes('Admin'); // true
 * ROLES.includes('Some Other Role'); // false
 */
export const ROLES = Object.values(Roles);

/**
 * The administrative roles, ordered from the least to the most privileged one.
 * Every other role sits below them and is administered by all three.
 * e.g. a KEY_COUNTRY_USER is less privileged than a ZONE_E_COMMERCE_STEWARD, who is in turn less privileged than an ADMIN.
 * In general roles can be managed by the role itself and the roles above it in the administrative hierarchy.
 */
export const ADMINISTRATIVE_ROLES: Roles[] = [Roles.KEY_COUNTRY_USER, Roles.ZONE_E_COMMERCE_STEWARD, Roles.ADMIN];

/**
 * Roles that grant access to teams. Every role except the requestor role grants team access.
 */
export const TEAM_ELIGIBLE_ROLES: readonly Roles[] = [
    Roles.ADMIN,
    Roles.TASK_OWNER,
    Roles.KEY_COUNTRY_USER,
    Roles.ZONE_E_COMMERCE_STEWARD
];
